import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Card, CardPreview, Image, Text, makeStyles, tokens, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { resolveUrl } from "../../utils/media-utils";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles({
  card: {
    width: "100%",
    maxWidth: "400px",
    cursor: "pointer",
    border: "1px solid transparent",
    ":hover": {
      boxShadow: tokens.shadow8,
      border: "1px solid var(--colorBrandBackground)"
    },
    transition: "box-shadow 0.3s ease-in-out",
    position: "relative",
    minHeight: "200px"
  },
  image: {
    width: "100%",
    minHeight: "200px",
    objectFit: "cover"
  },
  content: {
    bottom: "0",
    left: "0",
    padding: "12px 14px",
    position: "absolute",
    width: "100%",
    height: "auto!important",
    backgroundColor: "rgba(0, 0, 0, 0.5)"
  },
  title: {
    margin: "8px 0",
    fontWeight: "bold",
    color: tokens.colorNeutralForeground1
  },
  footer: {
    padding: "16px",
    display: "flex",
    justifyContent: "flex-end",
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`
  },
  link: {
    textDecoration: "none",
    color: tokens.colorNeutralForeground1,
    ":hover": {
      color: tokens.colorAccentForegroundHover,
      textDecoration: "underline"
    }
  },
  skeletonCard: {
    width: "100%",
    maxWidth: "400px",
    minHeight: "200px",
    boxShadow: tokens.shadow2,
    borderRadius: tokens.borderRadiusMedium
  },
  skeletonImage: {
    width: "100%",
    height: "200px",
    marginBottom: "12px"
  },
  skeletonTitle: {
    width: "70%",
    height: "20px",
    marginBottom: "8px"
  }
});

export function PortalItem({ portalUrl, onClick }) {
  const styles = useStyles();
  const [portalName, setPortalName] = useState("");
  const [portalThumbnail, setPortalThumbnail] = useState("");
  const [loading, setLoading] = useState(true); // Add loading state

  // Function to extract and format the portal name from the URL
  const getNameFromUrl = url => {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/").filter(Boolean);
      const lastSegment = pathSegments[pathSegments.length - 1] || "Portal";
      const formattedName = lastSegment
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return formattedName;
    } catch (error) {
      console.error("Invalid URL provided:", url);
      return "Portal";
    }
  };

  useEffect(() => {
    let isMounted = true; // To prevent setting state on unmounted component

    resolveUrl(portalUrl)
      .then(result => {
        if (isMounted) {
          setPortalName(getNameFromUrl(portalUrl));
          const thumbnail = proxiedUrlFor(result.meta.thumbnail);
          setPortalThumbnail(thumbnail);
          setLoading(false); // Data has been loaded
        }
      })
      .catch(error => {
        console.error("Error resolving URL:", error);
        if (isMounted) {
          setPortalName("Portal");
          setPortalThumbnail(""); // Optionally set a default thumbnail
          setLoading(false); // Even on error, stop loading
        }
      });

    return () => {
      isMounted = false; // Cleanup flag on unmount
    };
  }, [portalUrl]);

  if (loading) {
    // Render Skeleton while loading
    return (
      <div
        className={styles.skeletonCard}
        aria-busy="true"
        aria-label={<FormattedMessage id="portal.loading" defaultMessage="Loading portal..." />}
      >
        <Skeleton animation="wave">
          <SkeletonItem shape="rectangle" className={styles.skeletonImage} />
          <SkeletonItem shape="rectangle" className={styles.skeletonTitle} />
        </Skeleton>
      </div>
    );
  }

  // If thumbnail is not available, you can render a placeholder or omit the image
  if (!portalThumbnail) {
    return (
      <Card
        className={styles.card}
        onClick={onClick}
        onKeyDown={e => {
          if (e.key === "Enter") {
            window.open(portalUrl, "_blank");
          }
        }}
        tabIndex={0}
        aria-label={`Open ${portalName}`}
      >
        <CardPreview>
          <div
            style={{
              ...styles.image,
              backgroundColor: tokens.colorNeutralBackground3
            }}
          >
            <Text as="h3" className={styles.title}>
              {portalName}
            </Text>
          </div>
        </CardPreview>
      </Card>
    );
  }

  return (
    <Card
      className={styles.card}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === "Enter") {
          window.open(portalUrl, "_blank");
        }
      }}
      tabIndex={0}
      aria-label={`Open ${portalName}`}
    >
      <CardPreview>
        <Image src={portalThumbnail} alt={`${portalName} Thumbnail`} className={styles.image} />
        <div className={styles.content}>
          <Text as="h3" className={styles.title}>
            {portalName}
          </Text>
        </div>
      </CardPreview>
    </Card>
  );
}

PortalItem.propTypes = {
  portalUrl: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
